import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormAutosuggest, FormFancySelect } from '@fs/zion-form'
import { FEMALE, useGenderTranslations, getNestedSubelementsByType, MALE, UNKNOWN, INTERSEX } from '@fs/zion-sls'

export default function FormStandardizedSexField({
  fieldName = '',
  addedSexCode = {},
  person = {},
  handleOnFocus = () => {},
  handleOnBlur = () => {},
  handleOnChange = () => {},
  autosuggestProps = {},
  useDropdown = false,
  optionKeyName = 'key', // Allows the component to be used with different option structures, defaulting to 'key' for backward compatibility
}) {
  if (!person?.id && !addedSexCode?.id) throw Error('FormStandardizedSexField requires a person or addedSexCode')
  const [t] = useTranslation()
  const genderTranslations = useGenderTranslations()
  const isAddingSex = Boolean(addedSexCode?.id)
  const sexField = addedSexCode?.id ? addedSexCode : getNestedSubelementsByType(person, 'SEX_CODE')?.[0]
  const isEditable = isAddingSex || (sexField?.typeData?.editable ?? true)
  const sexSuggestions = useMemo(
    () => [
      {
        [optionKeyName]: UNKNOWN,
        primaryText: genderTranslations.unknown,
        standard: { standardizedText: genderTranslations.unknown, formalText: `http://gedcomx.org/${UNKNOWN}` },
      },
      {
        [optionKeyName]: MALE,
        primaryText: genderTranslations.male,
        standard: { standardizedText: genderTranslations.male, formalText: `http://gedcomx.org/${MALE}` },
      },
      {
        [optionKeyName]: FEMALE,
        primaryText: genderTranslations.female,
        standard: { standardizedText: genderTranslations.female, formalText: `http://gedcomx.org/${FEMALE}` },
      },
      {
        [optionKeyName]: INTERSEX,
        primaryText: genderTranslations.intersex,
        standard: { standardizedText: genderTranslations.intersex, formalText: `http://gedcomx.org/${INTERSEX}` },
      },
    ],
    [
      genderTranslations.female,
      genderTranslations.intersex,
      genderTranslations.male,
      genderTranslations.unknown,
      optionKeyName,
    ]
  )

  const sexDisplayLabel = t('edit-essential-info.sex-code', 'Sex')
  const sexLabel = sexField?.displayLabel || sexDisplayLabel

  if (useDropdown) {
    return (
      <FormFancySelect
        name={fieldName}
        data-testid="sex-field"
        label={sexLabel}
        options={sexSuggestions}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        disabled={!isEditable}
        placeholder={t('standardizedSexField.select-option', 'Select an option')}
        {...autosuggestProps}
      />
    )
  }

  return (
    <FormAutosuggest
      name={fieldName}
      data-testid="sex-field"
      label={sexLabel}
      suggestions={sexSuggestions}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      disabled={!isEditable}
      warningValidate={(value) => {
        if (!value || !value?.[optionKeyName])
          return t('standardizedSexField.validation.warning', 'No Standard Selected')
        return null
      }}
      validMessage={t('standardizedSexField.validation.success', 'Standard Sex')}
      {...autosuggestProps}
    />
  )
}
